import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config';
import { logger } from '../utils/logger';
import MatchCardFifa from "./MatchCardFifa";
import { MatchHistoryCard } from './MatchHistoryCard';
import { ActiveMatchCard } from './ActiveMatchCard';
import { theme } from '../theme';
import { Match } from '../types';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { useResponsive } from '../hooks/useResponsive';
import { AppHeader } from './AppHeader';
import { formatDateShort } from '../utils/helpers';
import { UnifiedHelpButton, SpecificHelpModal } from './Help';

// Componente de notificación
const Notification: React.FC<{
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
  onClose: () => void;
}> = ({ message, type, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const getStyle = () => ({
    position: 'fixed' as const,
    top: '20px',
    right: '20px',
    padding: '16px 20px',
    borderRadius: '8px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '14px',
    zIndex: 1000,
    maxWidth: '400px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer'
  });

  const typeStyles = {
    success: { backgroundColor: 'var(--success-color)' },
    error: { backgroundColor: 'var(--danger-color)' },
    warning: { backgroundColor: 'var(--warning-color)', color: 'var(--text-primary)' },
    info: { backgroundColor: 'var(--primary-color)' }
  };

  return (
    <button type="button" style={{ ...getStyle(), ...typeStyles[type], border: 'none' }} onClick={onClose}>
      <span>{type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}</span>
      <span>{message}</span>
    </button>
  );
};

// Componente de modal de confirmación
const ConfirmModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ isOpen, title, message, confirmText = 'Confirmar', cancelText = 'Cancelar', onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        maxWidth: '500px',
        width: '90%',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          color: theme.colors.text,
          fontFamily: theme.fonts.heading,
          fontSize: theme.fonts.sizeSubtitle,
          letterSpacing: theme.fonts.letterSpacingWide,
          textTransform: 'uppercase'
        }}>{title}</h3>
        <p style={{ 
          margin: '0 0 24px 0', 
          color: theme.colors.textSecondary,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeBody,
          lineHeight: 1.5
        }}>{message}</p>
        <div style={{ display: 'flex', gap: theme.spacing.sm, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.secondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.danger,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente de modal para transferir ownership
const TransferOwnershipModal: React.FC<{
  isOpen: boolean;
  match: Match | null;
  onTransfer: (newCreatorId: string) => void;
  onCancel: () => void;
}> = ({ isOpen, match, onTransfer, onCancel }) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');

  if (!isOpen || !match) return null;

  const otherPlayers = match.players.filter(p => p.id !== match.creatorId);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        maxWidth: '500px',
        width: '90%',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          color: theme.colors.text,
          fontFamily: theme.fonts.heading,
          fontSize: theme.fonts.sizeSubtitle,
          letterSpacing: theme.fonts.letterSpacingWide,
          textTransform: 'uppercase'
        }}>
          Transferir Ownership
        </h3>
        <p style={{ 
          margin: '0 0 16px 0', 
          color: theme.colors.textSecondary,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeBody
        }}>
          Selecciona a quién quieres transferir el ownership del partido:
        </p>
        <select
          value={selectedPlayerId}
          onChange={(e) => setSelectedPlayerId(e.target.value)}
          style={{
            width: '100%',
            padding: theme.spacing.sm,
            borderRadius: theme.borderRadius.button,
            border: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.card,
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            marginBottom: theme.spacing.md
          }}
        >
          <option value="">Selecciona un jugador...</option>
          {otherPlayers.map(player => (
            <option key={player.id} value={player.id}>
              {player.name}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: theme.spacing.sm, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.secondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={() => selectedPlayerId && onTransfer(selectedPlayerId)}
            disabled={!selectedPlayerId}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: selectedPlayerId ? theme.colors.warning : theme.colors.secondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: selectedPlayerId ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: selectedPlayerId ? 1 : 0.6
            }}
          >
            Transferir
          </button>
        </div>
      </div>
    </div>
  );
};

const MyMatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [matches, setMatches] = useState<Match[]>([]);
  const [archivedMatches, setArchivedMatches] = useState<Match[]>([]);
  const [userMatchHistory, setUserMatchHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  const [transferModal, setTransferModal] = useState<{ isOpen: boolean; match: Match | null }>({
    isOpen: false,
    match: null
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { modal, showConfirm, hideConfirm } = useConfirmModal();
  const [activeTab, setActiveTab] = useState<'activos' | 'historial'>('activos');
  const [showHelp, setShowHelp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMyMatches();
  }, []);

  useEffect(() => {
    if (activeTab === 'historial' && archivedMatches.length === 0) {
      fetchArchivedMatches();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchMyMatches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/matches/my-matches'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      } else {
        const errorData = await response.json();
        logger.error('Error fetching matches:', errorData);
      }
    } catch (error) {
      logger.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedMatches = async () => {
    setArchivedLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Obtener partidos archivados
      const matchesResponse = await fetch(getApiUrl('/api/matches/my-matches/archived?limit=100'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Obtener historial de partidos del usuario con estadísticas
      const historyResponse = await fetch(getApiUrl(`/api/users/${user?.id}/match-history?limit=100`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (matchesResponse.ok && historyResponse.ok) {
        const matchesData = await matchesResponse.json();
        const historyData = await historyResponse.json();
        
        setArchivedMatches(matchesData.matches || []);
        setUserMatchHistory(historyData.matches || []);
      } else {
        const errorData = await matchesResponse.json();
        logger.error('Error fetching archived matches:', errorData);
      }
    } catch (error) {
      logger.error('Error fetching archived matches:', error);
    } finally {
      setArchivedLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateShort(dateString);
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const getPlayerCount = (match: Match) => {
    return (match.players?.length || 0) + (match.substitutes?.length || 0);
  };

  const isCreator = (match: Match) => {
    return match.creatorId === user?.id;
  };

  const isUpcoming = (match: Match) => {
    const matchDate = new Date(match.date + 'T' + match.time);
    return matchDate > new Date();
  };

  // Función helper para obtener estadísticas del usuario para un partido específico
  const getUserStatsForMatch = (matchId: string) => {
    return userMatchHistory.find(history => history.matchId === matchId);
  };

  // Función para identificar el próximo partido (el más cercano en el futuro)
  const getNextMatch = (matches: Match[]) => {
    const now = new Date();
    const futureMatches = matches.filter(match => {
      const matchDate = new Date(match.date + 'T' + match.time);
      return matchDate > now;
    });
    
    if (futureMatches.length === 0) return null;
    
    return futureMatches.reduce((closest, current) => {
      const closestDate = new Date(closest.date + 'T' + closest.time);
      const currentDate = new Date(current.date + 'T' + current.time);
      return currentDate < closestDate ? current : closest;
    });
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ message, type, isVisible: true });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const showTransferModal = (match: Match) => {
    setTransferModal({ isOpen: true, match });
  };

  const hideTransferModal = () => {
    setTransferModal({ isOpen: false, match: null });
  };

  const handleTransferOwnership = async (newCreatorId: string) => {
    if (!transferModal.match) return;
    
    setActionLoading('transfer');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${transferModal.match.id}/transfer-ownership`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newCreatorId })
      });

      if (response.ok) {
        // No mostrar toast - el usuario puede ver que el ownership se transfirió porque la lista se actualiza
        hideTransferModal();
        fetchMyMatches();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al transferir ownership', 'error');
      }
    } catch (error) {
      logger.error('Error transferring ownership:', error);
      showNotification('Error al transferir ownership', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteMatch = async (match: Match) => {
    setActionLoading('delete');
    hideConfirm();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${match.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Partido eliminado exitosamente', 'success');
        fetchMyMatches();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al eliminar partido', 'error');
      }
    } catch (error) {
      logger.error('Error deleting match:', error);
      showNotification('Error al eliminar partido', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveMatch = async (match: Match) => {
    setActionLoading('leave');
    hideConfirm();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${match.id}/leave`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Te has dado de baja del partido correctamente', 'success');
        fetchMyMatches();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al abandonar partido', 'error');
      }
    } catch (error) {
      logger.error('Error leaving match:', error);
      showNotification('Error al abandonar partido', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActionClick = async (action: 'delete' | 'leave' | 'transfer', match: Match) => {
    try {
      if (action === 'delete') {
        const playerCount = getPlayerCount(match);
        const message = playerCount > 1 
          ? `¿Estás seguro de que quieres eliminar este partido? Esta acción eliminará el partido y todos los ${playerCount} jugadores. Esta acción no se puede deshacer.`
          : '¿Estás seguro de que quieres eliminar este partido? Esta acción no se puede deshacer.';
        const confirmed = await showConfirm(
          'Eliminar Partido',
          message,
          { confirmText: 'Sí, eliminar', cancelText: 'Cancelar' }
        );
        if (confirmed) handleDeleteMatch(match);
      } else if (action === 'leave') {
        const confirmed = await showConfirm(
          'Abandonar Partido',
          '¿Estás seguro de que quieres abandonar este partido?',
          { confirmText: 'Sí, abandonar', cancelText: 'Cancelar' }
        );
        if (confirmed) handleLeaveMatch(match);
      } else if (action === 'transfer') {
        showTransferModal(match);
      }
    } catch (error) {
      logger.error('🔍 Debug: Error en handleActionClick:', error);
    }
  };

  // Filtrar partidos activos e historial
  const activeMatchesAll = matches.filter(isUpcoming);
  // El historial ahora viene de archivedMatches
  const historyMatchesAll = archivedMatches;

  // Filtro de búsqueda case-insensitive sobre location + team names
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const matchesQuery = (m: Match) => {
    if (!trimmedQuery) return true;
    const haystack = [
      m.location || '',
      m.team1Name || '',
      m.team2Name || '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(trimmedQuery);
  };
  const activeMatches = activeMatchesAll.filter(matchesQuery);
  const historyMatches = historyMatchesAll.filter(matchesQuery);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      width: '100%',
      boxSizing: 'border-box',
      paddingBottom: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : '0', // Safe area para iOS + espacio para barra del navegador
    }}>
      <AppHeader />
      
      {/* Título */}
      <h1 style={{
        margin: isMobile ? '20px 12px' : '30px 20px',
        marginTop: isMobile ? 80 : 30,
        color: theme.colors.text,
        fontSize: isMobile ? '24px' : '32px',
        fontWeight: '600',
        textAlign: 'center',
        fontFamily: theme.fonts.heading
      }}>📅 Mis Partidos</h1>
      
      <div style={{
        padding: isMobile ? '12px' : theme.spacing.lg,
        paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : theme.spacing.lg, // Safe area adicional en el contenedor de contenido
        marginTop: 0
      }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: `2px solid ${theme.colors.border}`,
        }}>
          <button
            onClick={() => setActiveTab('activos')}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'activos' ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
              color: activeTab === 'activos' ? theme.colors.primary : theme.colors.textSecondary,
              fontWeight: activeTab === 'activos' ? 700 : 400,
              fontSize: isMobile ? '16px' : '18px',
              padding: '10px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              outline: 'none',
              fontFamily: theme.fonts.heading,
              letterSpacing: theme.fonts.letterSpacingWide,
              backgroundColor: 'transparent',
            }}
          >
            Partidos Activos
          </button>
          <button
            onClick={() => setActiveTab('historial')}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'historial' ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
              color: activeTab === 'historial' ? theme.colors.primary : theme.colors.textSecondary,
              fontWeight: activeTab === 'historial' ? 700 : 400,
              fontSize: isMobile ? '16px' : '18px',
              padding: '10px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              outline: 'none',
              fontFamily: theme.fonts.heading,
              letterSpacing: theme.fonts.letterSpacingWide,
              backgroundColor: 'transparent',
            }}
          >
            Historial
          </button>
        </div>

        {/* Search bar — filtra por lugar y nombres de equipos */}
        <div style={{
          position: 'relative',
          marginBottom: '20px',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '100%',
        }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por cancha o equipo..."
            data-testid="my-matches-search-input"
            style={{
              width: '100%',
              padding: isMobile ? '12px 40px 12px 40px' : '12px 44px 12px 44px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.input,
              fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              backgroundColor: theme.colors.background,
              color: theme.colors.text,
              boxSizing: 'border-box',
              outline: 'none',
              transition: 'border-color 0.2s ease',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.primary; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; }}
          />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.colors.textSecondary,
              fontSize: '16px',
              pointerEvents: 'none',
            }}
          >
            🔍
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Limpiar búsqueda"
              data-testid="my-matches-search-clear"
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: theme.colors.textSecondary,
                padding: '4px 8px',
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Main Content (modificado para tabs) */}
        {loading && activeTab === 'activos' ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔄</div>
              <p style={{ 
                fontFamily: theme.fonts.body,
                color: theme.colors.textSecondary
              }}>Cargando partidos...</p>
            </div>
          </div>
        ) : archivedLoading && activeTab === 'historial' ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔄</div>
              <p style={{
                fontFamily: theme.fonts.body,
                color: theme.colors.textSecondary
              }}>Cargando historial...</p>
            </div>
          </div>
        ) : (activeTab === 'activos' ? (
          activeMatches.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: '64px', marginBottom: '24px' }}>{trimmedQuery ? '🔍' : '⚽'}</div>
                <h2 style={{
                  margin: '0 0 16px 0',
                  fontFamily: theme.fonts.heading,
                  fontSize: isMobile ? '18px' : theme.fonts.sizeSubtitle,
                  color: theme.colors.text,
                  letterSpacing: theme.fonts.letterSpacingWide,
                  textTransform: 'uppercase'
                }}>
                  {trimmedQuery ? 'Sin resultados' : 'No tienes partidos activos'}
                </h2>
                <p style={{
                  margin: '0 0 24px 0',
                  fontFamily: theme.fonts.body,
                  fontSize: isMobile ? '14px' : theme.fonts.sizeBody,
                  color: theme.colors.textSecondary,
                  lineHeight: 1.5
                }}>
                  {trimmedQuery
                    ? `Ningún partido activo coincide con "${searchQuery.trim()}".`
                    : 'Crea un partido para empezar a jugar o únete a uno existente.'}
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : theme.spacing.md, 
                  justifyContent: 'center',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <button
                    onClick={() => navigate('/create-match')}
                    style={{
                      padding: isMobile ? '14px 20px' : `${theme.spacing.md} ${theme.spacing.lg}`,
                      backgroundColor: theme.colors.primary,
                      color: theme.colors.white,
                      border: 'none',
                      borderRadius: theme.borderRadius.button,
                      fontSize: isMobile ? '16px' : theme.fonts.sizeBody,
                      fontFamily: theme.fonts.body,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      justifyContent: 'center',
                      width: isMobile ? '100%' : 'auto',
                      minHeight: isMobile ? '48px' : 'auto'
                    }}
                  >
                    <span>➕</span>
                    Crear Partido
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(340px, 400px))',
              gap: isMobile ? '16px' : theme.spacing.lg,
              justifyContent: 'center',
              maxWidth: isMobile ? 'calc(100% - 24px)' : '1100px',
              margin: '0 auto',
              width: '100%',
              padding: isMobile ? '0 12px' : 0
            }}>
              {(() => {
                const nextMatch = getNextMatch(activeMatches);
                return activeMatches.map(match => (
                  <ActiveMatchCard
                    key={match.id}
                    match={match}
                    isNextMatch={nextMatch?.id === match.id}
                    isCreator={isCreator(match)}
                    onDelete={() => handleActionClick('delete', match)}
                    onLeave={() => handleActionClick('leave', match)}
                    onTransfer={() => handleActionClick('transfer', match)}
                    actionLoading={actionLoading === match.id}
                  />
                ));
              })()}
            </div>
          )
        ) : (
          // Historial
          historyMatches.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: '64px', marginBottom: '24px' }}>{trimmedQuery ? '🔍' : '📜'}</div>
                <h2 style={{
                  margin: '0 0 16px 0',
                  fontFamily: theme.fonts.heading,
                  fontSize: isMobile ? '18px' : theme.fonts.sizeSubtitle,
                  color: theme.colors.text,
                  letterSpacing: theme.fonts.letterSpacingWide,
                  textTransform: 'uppercase'
                }}>
                  {trimmedQuery ? 'Sin resultados' : 'No tienes historial de partidos'}
                </h2>
                <p style={{
                  margin: '0 0 24px 0',
                  fontFamily: theme.fonts.body,
                  fontSize: isMobile ? '14px' : theme.fonts.sizeBody,
                  color: theme.colors.textSecondary,
                  lineHeight: 1.5
                }}>
                  {trimmedQuery
                    ? `Ningún partido en tu historial coincide con "${searchQuery.trim()}".`
                    : 'Cuando termines de jugar partidos, aparecerán aquí.'}
                </p>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(340px, 400px))',
              gap: isMobile ? '16px' : theme.spacing.lg,
              justifyContent: 'center',
              maxWidth: isMobile ? 'calc(100% - 24px)' : '1100px',
              margin: '0 auto',
              width: '100%',
              padding: isMobile ? '0 12px' : 0
            }}>
              {historyMatches.map(match => {
                const userStats = getUserStatsForMatch(match.id);
                return (
                  <MatchHistoryCard
                    key={match.id}
                    match={match}
                    userStats={userStats ? {
                      goals: userStats.goals,
                      assists: userStats.assists,
                      saves: userStats.saves,
                      position: userStats.position,
                      teamName: userStats.teamName,
                      isMVP: userStats.isMVP,
                      isTopScorer: userStats.isTopScorer,
                      isTopAssister: userStats.isTopAssister,
                      isTopSaver: userStats.isTopSaver,
                      matchResult: userStats.matchResult,
                      hasPendingValidations: userStats.hasPendingValidations,
                      pendingValidationsCount: userStats.pendingValidationsCount
                    } : undefined}
                  />
                );
              })}
            </div>
          )
        ))}
      </div>
      {/* Modals and Notifications */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <ConfirmModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm || (() => {})}
        onCancel={modal.onCancel || hideConfirm}
      />
      <TransferOwnershipModal
        isOpen={transferModal.isOpen}
        match={transferModal.match}
        onTransfer={handleTransferOwnership}
        onCancel={hideTransferModal}
      />

              {/* Modal de Ayuda Contextual */}
        <SpecificHelpModal 
          isOpen={showHelp} 
          onClose={() => setShowHelp(false)} 
          context="myMatches"
        />

      </div>
    </div>
  );
};

export default MyMatchesPage; 